/*
 * App Actions
 *
 * Actions change things in your application
 * Since this boilerplate uses a uni-directional data flow, specifically redux,
 * we have these actions which are the only way your application interacts with
 * your application state. This guarantees that your state is up to date and nobody
 * messes it up weirdly somewhere.
 *
 * To add a new Action:
 * 1) Import your constant
 * 2) Add a function like this:
 *    export function yourAction(var) {
 *        return { type: YOUR_ACTION_CONSTANT, var: var }
 *    }
 */

import {
  UPDATE_CDF,
  SET_PPTX_CDF,
  SET_CONTROL_LIST,
  GET_QUESTION_SUGGESTIONS,
  UPDATE_QUESTION_SUGGESTIONS,
  CREATE_QUESTION_CONTROL,
  STORE_QUESTION_CONTROL_LIST,
  STORE_META_JSON,
  SET_META_DATA,
  CREATE_CHART_CONTROL,
  UPDATE_CLUSTER_LIST,
  GET_PRESET_COUNTRY_LIST,
  GET_PRESET_BREAK_LIST,
  STORE_PRESET_CLUSTER_LISTS,
  SET_PRESET_CLUSTER_CONTROL,
  IS_AUTHENTICATED,
  GET_PACKAGE,
  SET_PACKAGE,
  GET_USER,
  SET_USER,
  SET_ORG,
  SET_ROLE,
  GET_EXTERNAL_CDF,
  PPTX_DOWNLOAD_START,
  PPTX_DOWNLOAD_STOP,
  PPTX_DOWNLOAD_READY,
  PPTX_DOWNLOAD_FAILED,
  DOWNLOAD_NOTIFICATION_CLOSED,
  SET_SHOW_NEW_QUESTION,
  NEW_CHART,
  STORE_NEW_CHART,
  CREATE_TEMPORARY_CHART_CONTROL,
  STORE_TEMPORARY_QUSTION_CONTROL_LIST,
  CSV_DOWNLOAD_READY,
  CHART_LOADING,
  SET_PAGE_LOADING,
  CHART_ERROR,
} from './constants';


/**
* Call with field, value to set value of field to given value
* Call with null, value to set value of whole cdf to value
* Call with field, null to remove field from cdf
*
* If field is an array of objects with key value pairs in form
* {
*   field: 'CDF_FIELD',
*   value: VALUE_FOR_CDF,
* }
* it will iterate over the objects in the list and store the
* value in the specified field.
* A null value will remove that key from the CDF as normal.
*/
export function updateCDF(field, value) {
  return {
    type: UPDATE_CDF,
    field,
    value,
  };
}

export function setPptxCdf(value) {
  return {
    type: SET_PPTX_CDF,
    value,
  };
}

/**
* Sets controlList to specified controlList
*/
export function setControlList(controlList) {
  return {
    type: SET_CONTROL_LIST,
    value: controlList,
  };
}

export function showCsvDownload(url, code) {
  return {
    type: CSV_DOWNLOAD_READY,
    value: url,
    questionCode: code,
  };
}

export const setPageLoading = (value) => ({
  type: SET_PAGE_LOADING,
  value,
});

export function chartLoading() {
  return {
    type: CHART_LOADING,
    value: true,
  };
}

export function chartLoadingComplete() {
  return {
    type: CHART_LOADING,
    value: false,
  };
}

export function dispatchError(message) {
  return {
    type: CHART_ERROR,
    value: message,
  };
}

export function setPackage(ffPackage) {
  return {
    type: SET_PACKAGE,
    value: ffPackage,
  };
}

export function setUser(ffUser) {
  return {
    type: SET_USER,
    value: ffUser,
  };
}

export function setOrg(ffOrg) {
  return {
    type: SET_ORG,
    value: ffOrg,
  };
}

export function setRole(ffRole) {
  return {
    type: SET_ROLE,
    value: ffRole,
  };
}

export function getQuestionSuggestions(text) {
  return {
    type: GET_QUESTION_SUGGESTIONS,
    value: text,
  };
}

export function updateQuestionSuggestions(questions) {
  return {
    type: UPDATE_QUESTION_SUGGESTIONS,
    value: questions,
  };
}

export function createQuestionControl(code) {
  return {
    type: CREATE_QUESTION_CONTROL,
    code,
  };
}


export function storeQuestionControlList(cl) {
  return {
    type: STORE_QUESTION_CONTROL_LIST,
    value: cl,
  };
}

export function storeTemporaryQuestionControlList(cl) {
  return {
    type: STORE_TEMPORARY_QUSTION_CONTROL_LIST,
    value: cl,
  };
}

export function storeMetaJSON(meta) {
  return {
    type: STORE_META_JSON,
    value: meta,
  };
}

export function setMetaData(metaData, questionCode) {
  return {
    type: SET_META_DATA,
    metaData,
    questionCode,
  };
}

export function createChartControl(visId, code, kidsWave) {
  return {
    type: CREATE_CHART_CONTROL,
    visId,
    code,
    kidsWave,
  };
}

export function createTemporaryChartControl(visId, code, kidsWave) {
  return {
    type: CREATE_TEMPORARY_CHART_CONTROL,
    visId,
    code,
    kidsWave,
  };
}


export function updateClusterList(index, value, field) {
  return {
    type: UPDATE_CLUSTER_LIST,
    index,
    value,
    field,
  };
}

export function getPresetCountryList(data) {
  return {
    type: GET_PRESET_COUNTRY_LIST,
    ...data,
  };
}

export function getPresetBreakList(country, controlList, kidsWave) {
  return {
    type: GET_PRESET_BREAK_LIST,
    country,
    controlList,
    kidsWave,
  };
}

export function storePresetClusterLists(field, list) {
  return {
    type: STORE_PRESET_CLUSTER_LISTS,
    field,
    list,
  };
}

export function setPresetClusterControl(field, value, list) {
  return {
    type: SET_PRESET_CLUSTER_CONTROL,
    field,
    value,
    list,
  };
}

export function isAuthenticated(path) {
  return {
    type: IS_AUTHENTICATED,
    path,
  };
}

export function getPackage() {
  return {
    type: GET_PACKAGE,
  };
}

export function getUser() {
  return {
    type: GET_USER,
  };
}

export function getExternalCdf(contentId) {
  return {
    type: GET_EXTERNAL_CDF,
    contentId,
  };
}

export function startPptxDownload(cdf) {
  return {
    type: PPTX_DOWNLOAD_START,
    cdf,
  };
}

export function stopPptxDownload(cdf) {
  return {
    type: PPTX_DOWNLOAD_STOP,
    cdf,
  };
}

export function getPptxDownloadSuccessAction(pptxUrl) {
  return {
    type: PPTX_DOWNLOAD_READY,
    pptxUrl,
  };
}

export function getPptxDownloadFailureAction(payload) {
  return {
    type: PPTX_DOWNLOAD_FAILED,
    payload,
  };
}

export function getDownloadNotificationCloseAction(payload) {
  return {
    type: DOWNLOAD_NOTIFICATION_CLOSED,
    payload,
  };
}

export function setShowNewQuestion(value) {
  return {
    type: SET_SHOW_NEW_QUESTION,
    value,
  };
}

export function newChart(cdf) {
  return {
    type: NEW_CHART,
    cdf,
  };
}

export function storeNewChart(cdf, controlList, respondToBreakGroupChange) {
  return {
    type: STORE_NEW_CHART,
    cdf,
    controlList,
    respondToBreakGroupChange,
  };
}



// WEBPACK FOOTER //
// ./app/containers/App/actions.js